import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Edit2, Trash2, Eye, Image as ImageIcon, Repeat } from "lucide-react";
import { format } from "date-fns";
import { Activity } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { PermissionGuard } from "@/components/guards/PermissionGuard";

interface ActivityCardProps {
  activity: Activity;
  onView: (activity: Activity) => void;
  onEdit: (activity: Activity) => void;
  onDelete: (activity: Activity) => void;
}

export function ActivityCard({ activity, onView, onEdit, onDelete }: ActivityCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="overflow-hidden h-full flex flex-col group hover:shadow-md transition-all duration-200 border-border/50 rounded-lg">
        {/* Activity Image */}
        <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5">
          {activity.imageUrl ? (
            <img
              src={activity.imageUrl}
              alt={activity.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-pointer"
              onClick={() => onView(activity)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ImageIcon className="w-24 h-24 opacity-20" />
            </div>
          )}

          {/* Recurring Badge */}
          {activity.isRecurring && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium">
                <span className="flex items-center gap-1">
                  <Repeat className="w-3 h-3" />
                  Recurring
                </span>
              </Badge>
            </div>
          )}
        </div>

        <CardContent className="p-5 flex-1 flex flex-col">
          {/* Title */}
          <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {activity.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-muted-foreground mb-4 line-clamp-3 flex-1">
            {activity.description}
          </p>

          {/* Date Info */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4 pb-4 border-b border-border">
            <Calendar className="w-3 h-3" />
            {activity.isRecurring ? (
              <span>Regularly scheduled</span>
            ) : (
              <span>{format(new Date(activity.date), "MMM d, yyyy")}</span>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(activity)}
              className="flex-1"
            >
              <Eye className="w-4 h-4 mr-2" />
              View
            </Button>
            <PermissionGuard resource="activities" action="update">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(activity)}
                className="flex-1"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="activities" action="delete">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(activity)}
                className="hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </PermissionGuard>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}